import cn from 'clsx';
import {useRouter} from 'next/router';
import PropTypes from 'prop-types';
import React, {useCallback, useMemo} from 'react';
import {Collapse as CollapseFromLibrary} from 'react-collapse';

import ArrowLeft from '@/public/images/prev-video-arror.svg';
import {getAuthorizationToken} from '@/src/app/utils/authorizationToken';
import CoAuthorsList from '@/src/coAuthors/components/CoAuthorsList';
import useIsMobile from '@/src/hooks/useIsMobile';
import useRoutePaths from '@/src/hooks/useRoutePaths';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import ContentCreatorWidget from '../../contentCreatorWidget/containers/ContentCreatorWidget';
import CourseInfo from '../../courseDetails/components/CourseInfo';
import ReviewsList from '../../reviewsWidget/containers/ReviewsList';
import {Separator, Text} from '../../ui';
import CourseVideo from '../containers/CourseVideo';
import css from '../styles/WatchCourseLayout.module.css';
import Topics from './Topics/Topics';

const WatchCourseLayout = ({
  topics,
  course,
  courseId,
  currentTopic,
  currentVideo,
  onTopicClick,
  onTopicVideoClick,
  updateDate,
  contentCreator,
  isWritingReviewAllowed,
  translationId,
  toggleLeaveReviewAllowed,
  onTopicAttachmentClick,
  onStoppedWatchVideo,
  onPreviousVideoClick,
  onNextVideoClick,
  isPreviousDisable,
  isNextDisable,
  onVideoWathced,
}) => {
  const {isMaxWidth1100} = useIsMobile();
  const {query, push} = useRouter();
  const {COURSES_ROUTE} = useRoutePaths();
  const isUserAuthorized = getAuthorizationToken();

  const handleBackButtonClick = useCallback(() => {
    // @ts-ignore
    push(COURSES_ROUTE.getLink(query.slug));
  }, [query.slug]);

  const pageTitleNode = useMemo(
    () => (
      <>
        <Text
          fontSize={Text.FONT_SIZE.SIZE_36}
          fontWeight={Text.FONT_WEIGHT.MEDIUM}
          lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
        >
          {currentTopic.title}
        </Text>
        <div className={css.lastUpdateWrapper}>
          <Text
            fontSize={Text.FONT_SIZE.SIZE_16}
            fontWeight={Text.FONT_WEIGHT.NORMAL}
            color={Text.COLOR.GREY}
            lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
          >
            <TextLocal
              id={'courseDetails.updateDate'}
              params={{date: updateDate}}
            />
          </Text>
          {course.surgeryDate && (
            <Text
              fontSize={Text.FONT_SIZE.SIZE_16}
              fontWeight={Text.FONT_WEIGHT.NORMAL}
              color={Text.COLOR.GREY}
              lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
            >
              <TextLocal id={'courseDetails.date-of-surgery'} />:{' '}
              {course.surgeryDate}
            </Text>
          )}
        </div>
      </>
    ),
    [currentTopic.title, updateDate]
  );

  return (
    <div className={css.wrapper}>
      <div className={css.courseMain}>
        <span className={css.videoPlayer}>
          <CollapseFromLibrary
            isOpened={Boolean(currentVideo)}
            theme={{collapse: css.collapseContent}}
          >
            <CourseVideo
              currentVideo={currentVideo}
              isPreviousDisable={isPreviousDisable}
              isNextDisable={isNextDisable}
              // @ts-ignore
              onTopicAttachmentClick={onTopicAttachmentClick}
              onStoppedWatchVideo={onStoppedWatchVideo}
              onPreviousVideoClick={onPreviousVideoClick}
              onNextVideoClick={onNextVideoClick}
              onVideoWathced={onVideoWathced}
            />
          </CollapseFromLibrary>
        </span>
        <div
          className={cn(css.backWrapper, {
            [css.backWrapperTransparent]: !currentVideo,
          })}
        >
          <div className={css.courseTitle}>
            <button onClick={handleBackButtonClick} className={css.backButton}>
              <ArrowLeft />
              <Text
                fontSize={Text.FONT_SIZE.SIZE_16}
                fontWeight={Text.FONT_WEIGHT.BOLD}
              >
                <TextLocal id={'create-course.back'} />
              </Text>
            </button>
            {currentVideo && (
              <Text
                fontWeight={Text.FONT_WEIGHT.THIN}
                fontSize={Text.FONT_SIZE.SIZE_16}
              >
                {course.title}
              </Text>
            )}
          </div>
        </div>
        {isMaxWidth1100 && (
          <div className={css.mobileTitle}>{pageTitleNode}</div>
        )}
        <div className={css.topics}>
          {Boolean(topics.length) && (
            <Topics
              currentTopic={currentTopic}
              contentCreator={contentCreator}
              topics={topics}
              onTopicClick={onTopicClick}
              onTopicVideoClick={onTopicVideoClick}
              selectedVideoId={currentVideo?.id}
              onTopicAttachmentClick={onTopicAttachmentClick}
            />
          )}
        </div>
        <div className={css.info}>
          <div className={css.pageTitle}>
            {!isMaxWidth1100 && (
              <>
                {pageTitleNode}
                <div className={css.separatorWrapper}>
                  <Separator borderWeight={Separator.BORDER_WEIGHT.THIN} />
                </div>
              </>
            )}

            <div className={css.topicsDescription}>
              {Boolean(currentTopic?.description?.length) && (
                <CourseInfo
                  title={'watchCourse.description'}
                  text={currentTopic.description}
                />
              )}
              {Boolean(currentTopic?.technicalFeature?.length) && (
                <CourseInfo
                  title={'watchCourse.tech-feature'}
                  text={currentTopic.technicalFeature}
                />
              )}

              <div>
                <div className={css.contentCreatorTitle}>
                  <Text
                    fontSize={Text.FONT_SIZE.SIZE_36}
                    fontFamily={Text.FONT_FAMILY.RUBIK}
                    lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
                    tagComponent={Text.TAG.SPAN}
                    fontWeight={Text.FONT_WEIGHT.MEDIUM}
                  >
                    <TextLocal id={'courseDetails.content-creator'} />
                  </Text>
                </div>
                <ContentCreatorWidget
                  userId={contentCreator.id}
                  isShowUserCourses
                  excludedCourses={[courseId]}
                  isAllowToFollow={Boolean(isUserAuthorized)}
                />
              </div>

              <CoAuthorsList list={course.coAuthors} />

              <ReviewsList
                key={courseId}
                toggleLeaveReviewAllowed={toggleLeaveReviewAllowed}
                isWritingReviewAllowed={
                  isWritingReviewAllowed && Boolean(isUserAuthorized)
                }
                courseId={translationId}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

WatchCourseLayout.propTypes = {
  topics: PropTypes.array.isRequired,
  currentTopic: PropTypes.object.isRequired,
  currentVideo: PropTypes.object.isRequired,
  onTopicClick: PropTypes.func.isRequired,
  onTopicVideoClick: PropTypes.func.isRequired,
  updateDate: PropTypes.string.isRequired,
  contentCreator: PropTypes.object.isRequired,
  isWritingReviewAllowed: PropTypes.bool.isRequired,
  onTopicAttachmentClick: PropTypes.func.isRequired,
  onStoppedWatchVideo: PropTypes.func.isRequired,
  onPreviousVideoClick: PropTypes.func.isRequired,
  onVideoWathced: PropTypes.func.isRequired,
  toggleLeaveReviewAllowed: PropTypes.func.isRequired,
  onNextVideoClick: PropTypes.func.isRequired,
  isPreviousDisable: PropTypes.bool.isRequired,
  isNextDisable: PropTypes.bool.isRequired,
  courseId: PropTypes.number,
  translationId: PropTypes.number,
  course: PropTypes.object.isRequired,
};

export default WatchCourseLayout;
